import React, { CSSProperties, FC } from "react";
import {
  Task,
  TaskPriority,
  TaskStatus,
  TaskViewField,
  TaskWithOrganization,
} from "@dewo/app/graphql/types";
import { Row, Tag, Typography } from "antd";
import * as Colors from "@ant-design/colors";
import moment from "moment";
import { TaskRewardTag } from "../reward/TaskRewardTag";
import { SkillTag } from "@dewo/app/components/tags/SkillTag";
import { SubtaskTagButton } from "./SubtaskTagButton";
import { StoryPointIcon } from "@dewo/app/components/icons/task/StoryPoint";
import { DropdownSelect } from "@dewo/app/components/form/DropdownSelect";
import { usePermission } from "@dewo/app/contexts/PermissionsContext";
import { useUpdateTask } from "../hooks";
import { PRIORITY_LABEL } from "../board/util";
import { TaskPriorityIcon } from "@dewo/app/components/icons/task/TaskPriority";
import { OrganizationAvatar } from "@dewo/app/components/avatars/OrganizationAvatar";
import { useTaskViewContext } from "../views/TaskViewContext";
import {
  AppstoreOutlined,
  CalendarOutlined,
  CopyOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import { TaskOpenToBidsTag } from "@dewo/app/components/task/TaskOpenToBidsTag";

interface Props {
  task: Task | TaskWithOrganization;
  fields?: Set<TaskViewField>;
  skills?: "default" | "emoji";
  style?: CSSProperties;
  expanded?: boolean;
  onToggleSubtasks?(): void;
  showPriority?: boolean;
}

const defaultFields = new Set([TaskViewField.tags, TaskViewField.skills]);

export const TaskTagsRow: FC<Props> = ({
  task,
  fields = defaultFields,
  skills,
  style,
  expanded,
  onToggleSubtasks,
  showPriority,
}) => {
  const currentViewWorkspaceId = useTaskViewContext().currentView?.workspaceId;
  const canChangePriority = usePermission("update", task, "priority");
  const updateTask = useUpdateTask();
  const tagComponentsToRender = [
    !!showPriority && fields.has(TaskViewField.priority) && (
      <DropdownSelect
        key="priority"
        mode="default"
        disabled={!canChangePriority}
        onChange={(priority: TaskPriority) =>
          updateTask({ id: task.id, priority }, task)
        }
        options={(Object.keys(PRIORITY_LABEL) as TaskPriority[]).map(
          (priority) => ({
            value: priority,
            label: (
              <Row align="middle" style={{ columnGap: 8 }}>
                <TaskPriorityIcon priority={priority} size={13} />
                {PRIORITY_LABEL[priority]}
              </Row>
            ),
          })
        )}
      >
        <Tag title={PRIORITY_LABEL[task.priority]} style={{ height: 20 }}>
          <TaskPriorityIcon priority={task.priority} size={13} />
        </Tag>
      </DropdownSelect>
    ),
    fields.has(TaskViewField.dueDate) && !!task.dueDate && (
      <Tag key="dueDate">
        {task.status !== TaskStatus.DONE &&
        moment().endOf("day").isAfter(task.dueDate) ? (
          <ExclamationCircleFilled style={{ color: Colors.red.primary }} />
        ) : (
          <CalendarOutlined />
        )}
        <span>{moment(task.dueDate).format("D MMM")}</span>
      </Tag>
    ),
    !!task.storyPoints && (
      <Tag key="storyPoints">
        <StoryPointIcon />
        <span>{task.storyPoints}</span>
      </Tag>
    ),
    !!task.subtasks.length && (
      <SubtaskTagButton
        key="subtasks"
        subtasks={task.subtasks}
        expanded={expanded}
        onToggle={onToggleSubtasks}
      />
    ),
    ...(fields.has(TaskViewField.skills)
      ? task.skills.map((skill) => (
          <SkillTag key={skill.id} mode={skills} skill={skill} />
        ))
      : []),
    fields.has(TaskViewField.organization) && "organization" in task.workspace && (
      <Tag key="workspace" style={{ paddingLeft: 0 }}>
        <OrganizationAvatar
          organization={task.workspace.organization}
          size={20}
        />
        <Typography.Text style={{ marginLeft: 4 }}>
          {task.workspace.organization.name}
        </Typography.Text>
      </Tag>
    ),
    fields.has(TaskViewField.workspace) &&
      task.workspaceId !== currentViewWorkspaceId && (
        <Tag key={task.workspaceId} icon={<AppstoreOutlined />}>
          {task.workspace.name}
        </Tag>
      ),
    ...(fields.has(TaskViewField.tags)
      ? task.tags
          .filter((tag) => !tag.deletedAt)
          .map((tag) => (
            <Tag key={tag.id} color={tag.color}>
              {tag.label}
            </Tag>
          ))
      : []),
    ...(fields.has(TaskViewField.reward)
      ? task.rewards.map((r) => <TaskRewardTag key={r.id} reward={r} />)
      : []),
    fields.has(TaskViewField.reward) &&
      task.openToBids &&
      !task.rewards.length && <TaskOpenToBidsTag key="bids" />,
    !!fields.has(TaskViewField.template) && !!task.templateTask && (
      <Tag
        key="template"
        icon={<CopyOutlined />}
        title={`Template: ${task.templateTask.name}`}
      >
        {task.templateTask.name}
      </Tag>
    ),
  ].filter((c) => !!c);

  if (!tagComponentsToRender.length) return null;
  return (
    <Row style={{ ...style, marginLeft: 0, marginRight: 0, rowGap: 4 }}>
      {tagComponentsToRender}
    </Row>
  );
};
